import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "virus-malware-ordinateur",
  silo: "securite",
  isPillar: true,
  h1: "Mon ordinateur a un virus : comment le détecter et le supprimer ?",
  metaTitle: "Virus sur ordinateur : détecter et supprimer un malware",
  metaDescription: "PC infecté par un virus ou malware ? Signes d'alerte, méthodes de suppression et protection. Intervention rapide à domicile ou sur site.",
  excerpt: "Un virus sur un PC ne se voit pas toujours immédiatement. Lenteur inexpliquée, publicités intempestives, mots de passe volés... Voici comment reconnaître une infection et agir efficacement.",
  publishedAt: "2024-02-20",
  readingMinutes: 7,
  relatedSlugs: ["publicites-popups-intempestifs", "erreurs-windows-frequentes", "pc-lent-windows-10-11"],
  faq: [
    {
      question: "Comment savoir si mon PC a un virus sans antivirus ?",
      answer: "Plusieurs signes trahissent une infection : PC soudainement très lent, programmes qui s'ouvrent seuls, publicités qui apparaissent partout, page d'accueil du navigateur modifiée, ou votre antivirus qui a été désactivé sans votre intervention.",
    },
    {
      question: "Windows Defender est-il suffisant pour se protéger ?",
      answer: "Windows Defender offre une protection de base correcte s'il est à jour. Mais pour une protection renforcée, notamment contre les ransomwares et les malwares récents, un antivirus complémentaire comme Malwarebytes est recommandé.",
    },
    {
      question: "Un virus peut-il voler mes mots de passe et données bancaires ?",
      answer: "Oui, certains malwares (chevaux de Troie, keyloggers) sont spécialement conçus pour récupérer vos identifiants, mots de passe et informations bancaires. Si vous suspectez une telle infection, changez vos mots de passe depuis un autre appareil immédiatement.",
    },
    {
      question: "Faut-il formater le PC après un virus ?",
      answer: "Non, dans la grande majorité des cas. Un nettoyage professionnel avec les bons outils suffit à éliminer l'infection complètement, sans perte de données. La réinstallation n'est nécessaire que pour les infections très graves (rootkits persistants).",
    },
    {
      question: "Comment mon PC a-t-il attrapé un virus ?",
      answer: "Les voies d'infection les plus courantes : pièce jointe email infectée, téléchargement depuis un site non officiel, fausse mise à jour logicielle, site web compromis, ou clé USB infectée.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Les virus et malwares sont aujourd'hui plus discrets qu'avant. Fini le temps des virus qui affichent des messages à l'écran -- les infections modernes travaillent en silence, volant vos données ou utilisant votre PC pour des activités illicites sans que vous le sachiez.",
    },
    {
      type: "h2",
      text: "Les signes qui indiquent que votre PC est infecté",
    },
    {
      type: "ul",
      items: [
        "PC soudainement très lent sans raison apparente",
        "Programmes qui se lancent ou se ferment sans que vous l'ayez demandé",
        "Page d'accueil de votre navigateur changée (Bing, Yahoo ou autre moteur inconnu)",
        "[Publicités et pop-ups](slug:publicites-popups-intempestifs) qui apparaissent partout, même sur des sites normaux",
        "Windows Defender ou votre antivirus désactivé",
        "[Erreurs Windows](slug:erreurs-windows-frequentes) qui apparaissent fréquemment",
        "Votre messagerie envoie des emails à votre insu",
        "PC qui chauffe anormalement même au repos",
      ],
    },
    {
      type: "h2",
      text: "Les différents types de malwares à connaître",
    },
    {
      type: "h3",
      text: "Virus et vers",
    },
    {
      type: "p",
      text: "Les virus classiques se reproduisent et infectent d'autres fichiers. Les vers se propagent automatiquement sur le réseau, sans intervention de l'utilisateur.",
    },
    {
      type: "h3",
      text: "Chevaux de Troie (Trojans)",
    },
    {
      type: "p",
      text: "Se déguisent en logiciels légitimes pour s'installer. Une fois en place, ils ouvrent une porte dérobée permettant aux pirates de contrôler votre PC à distance.",
    },
    {
      type: "h3",
      text: "Ransomwares",
    },
    {
      type: "p",
      text: "Parmi les plus dangereux : ils chiffrent tous vos fichiers et exigent une rançon pour vous les rendre. Aucune garantie de récupérer vos données même en payant. La prévention est essentielle.",
    },
    {
      type: "h3",
      text: "Adwares et spywares",
    },
    {
      type: "p",
      text: "Les adwares inondent votre écran de publicités. Les spywares espionnent votre activité (sites visités, mots de passe tapés, conversations) et envoient ces données aux pirates.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Urgence : si vous pensez être victime d'un ransomware",
      text: "Éteignez immédiatement votre PC et débranchez-le du réseau. N'essayez pas de continuer à l'utiliser. Contactez-nous de suite -- chaque minute compte pour limiter les dégâts.",
    },
    {
      type: "h2",
      text: "Comment supprimer un virus : étapes à suivre",
    },
    {
      type: "ol",
      items: [
        "Lancez une analyse complète avec Windows Defender (Sécurité Windows > Protection antivirus)",
        "Téléchargez et lancez Malwarebytes (version gratuite) pour une deuxième vérification",
        "Si un malware est détecté, suivez les instructions de suppression et redémarrez",
        "Vérifiez vos extensions de navigateur et supprimez celles que vous ne reconnaissez pas",
        "Changez vos mots de passe importants (email, banque) depuis un autre appareil si nécessaire",
        "Mettez à jour Windows et tous vos logiciels pour fermer les failles exploitées",
      ],
    },
    {
      type: "h2",
      text: "Protection : comment éviter les infections futures",
    },
    {
      type: "ul",
      items: [
        "Ne jamais ouvrir une pièce jointe email inattendue, même d'un expéditeur connu",
        "Télécharger les logiciels uniquement depuis les sites officiels des éditeurs",
        "Maintenir Windows et votre antivirus à jour en permanence",
        "Sauvegarder régulièrement vos données sur un disque externe non connecté",
        "Ne jamais cliquer sur des pop-ups qui vous annoncent que votre PC est infecté",
      ],
    },
    {
      type: "h2",
      text: "Intervention à domicile si le virus résiste",
    },
    {
      type: "p",
      text: "Certaines infections sont tenaces et résistent aux outils grand public. Nos techniciens disposent d'outils professionnels de désinfection et d'analyse en profondeur. Nous intervenons à domicile ou sur site, vous voyez tout ce que nous faisons, et votre PC repart propre -- sans perte de données dans la grande majorité des cas.",
    },
  ],
};

export default article;
